import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api.ts';
import { loadGoogleIdentity, type GoogleCredentialResponse } from '../lib/google.ts';
import { stagingEnv } from '../lib/stagingGuard.ts';

/**
 * The only way in.
 *
 * There is no form here and nothing to type. Every failure this screen can show
 * is somebody else's to fix — a server missing its Google settings, an account
 * on the wrong domain, a person not on the People list — so each message names
 * the fix rather than the fault.
 */
export default function Login({ onAuthed }: { onAuthed: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const googleSlot = useRef<HTMLDivElement>(null);

  // Whether this server offers Google sign-in at all. `stagingEnv` is the one
  // GET /api/env the client makes, and it fails open, so `checked` is what
  // separates "still asking" from "asked, and there is no way in".
  useEffect(() => {
    let live = true;
    stagingEnv().then((env) => {
      if (!live) return;
      setClientId(env.googleClientId ?? null);
      setChecked(true);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const slot = googleSlot.current;
    if (!clientId || !slot) return;
    let live = true;
    loadGoogleIdentity()
      .then((gis) => {
        if (!live) return;
        gis.accounts.id.initialize({
          client_id: clientId,
          callback: (response: GoogleCredentialResponse) => {
            if (!response.credential) return setError('Google returned no credential. Try again.');
            setBusy(true);
            setError(null);
            api
              .post('/api/auth/google', { credential: response.credential })
              .then(() => onAuthed())
              .catch((err) => setError(errorMessage(err)))
              .finally(() => setBusy(false));
          },
        });
        gis.accounts.id.renderButton(slot, {
          theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'filled_black' : 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: 280,
        });
      })
      .catch(() => {
        if (live) setError('Could not reach Google. Check your connection and reload.');
      });
    return () => {
      live = false;
    };
  }, [clientId, onAuthed]);

  return (
    <div className="login-shell">
      <div className="login-card">
        <h1>RKH Studio</h1>
        {clientId ? (
          <>
            <p className="muted">Sign in with your RKH Google account.</p>
            <div className="login-card__google" ref={googleSlot} aria-busy={busy} />
          </>
        ) : (
          <p className="muted">
            {checked
              ? 'This studio has no sign-in configured. Ask whoever deployed it to set the Google client id and domain.'
              : 'Loading…'}
          </p>
        )}
        {error && (
          <div className="error login-card__error" role="alert">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
